import { withDevtools, withGlitchTracking, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import {
  DefaultFuelSpec,
  FUEL_SPECS_STORE_ID,
  FuelSpecsStoreState,
  INITIAL_FUEL_SPEC_STORE_STATE,
} from '@navstation-portal/fuel-specs/data-model/fuel-spec';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { signalStore, type, withComputed, withHooks, withState } from '@ngrx/signals';
import { entityConfig, withEntities } from '@ngrx/signals/entities';
import { injectDispatch } from '@ngrx/signals/events';
import { fuelSpecsEvents } from './fuel-specs-events';
import {
  buildFuelSpecLookupIdMap,
  buildFuelSpecsVm,
  getFuelSpecByLookup,
  getFuelSpecValuesUtil,
} from './fuel-specs-vm-builders';
import { withFuelSpecsEffects } from './with-fuel-specs-effects';
import { withFuelSpecsReducers } from './with-fuel-specs-reducers';

const FUEL_SPECS_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const fuelSpecEntityConfig = entityConfig({
  entity: type<DefaultFuelSpec>(),
  collection: 'fuelSpec',
  selectId: (fuelSpec: DefaultFuelSpec) => fuelSpec._id,
});

export const FuelSpecsStore = signalStore(
  { providedIn: 'root' },
  withDevtools(FUEL_SPECS_STORE_ID, withGlitchTracking()),
  withState<FuelSpecsStoreState>(INITIAL_FUEL_SPEC_STORE_STATE),
  withEntities(fuelSpecEntityConfig),
  withStorageSync(FUEL_SPECS_STORE_ID),
  withFuelSpecsReducers(),
  withFuelSpecsEffects(),
  withHooks((store) => {
    const fuelSpecsDispatcher = injectDispatch(fuelSpecsEvents);

    return {
      onInit: () => {
        const lastFetched = store.lastFetched();
        const isCacheExpired = !lastFetched || Date.now() - lastFetched > FUEL_SPECS_CACHE_TTL_MS;
        const hasEntities = store.fuelSpecIds().length > 0;
        const hasLookup = Object.keys(store.fuelSpecByGradeAndIso()).length > 0;

        if (isCacheExpired) {
          fuelSpecsDispatcher.fetchDefaultFuelSpecs();
          return;
        }

        if (hasEntities && !hasLookup) {
          fuelSpecsDispatcher.rebuildFuelSpecLookup();
        }
      },
    };
  }),
  withComputed((store) => {
    const fuelSpecLookupByGradeAndIso = computed(() => {
      const persistedLookup = store.fuelSpecByGradeAndIso();
      if (Object.keys(persistedLookup).length > 0) {
        return persistedLookup;
      }

      // Backfill lookup from entities when cache hydration predates lookup field.
      return buildFuelSpecLookupIdMap(store.fuelSpecEntityMap());
    });

    return {
      fuelSpecLookupByGradeAndIso,
      getFuelSpecBySelection: computed(() => {
        const fuelSpecLookup = fuelSpecLookupByGradeAndIso();
        const fuelSpecs = store.fuelSpecEntityMap();

        return (fuelType?: SelectOption, isoFuelGrade?: SelectOption) =>
          getFuelSpecByLookup(
            fuelSpecLookup,
            fuelSpecs,
            fuelType?.code as string | undefined,
            isoFuelGrade?.code as string | undefined
          );
      }),
      getFuelSpecValues: computed(() => {
        const fuelSpecLookup = fuelSpecLookupByGradeAndIso();
        const fuelSpecs = store.fuelSpecEntityMap();

        return (fuelType?: SelectOption, isoFuelGrade?: SelectOption) =>
          getFuelSpecValuesUtil(fuelType, isoFuelGrade, fuelSpecLookup, fuelSpecs);
      }),
      fuelspecsVm: computed(() => {
        const fuelSpecs = store.fuelSpecEntityMap();
        const fuelSpecLookup = fuelSpecLookupByGradeAndIso();

        return buildFuelSpecsVm(fuelSpecs, fuelSpecLookup);
      }),
    };
  })
);
